import { Link } from "react-router-dom";
import { Phone, Mail, Globe, MapPin, Flame, Camera, DoorOpen, Radio } from "lucide-react";
import FSSLogo from "./FSSLogo";

const COLS = [
  {
    title: "Fire Safety",
    icon: Flame,
    links: [
      { to: "/services/fire-alarms", label: "Fire Alarms" },
      { to: "/services/emergency-lighting", label: "Emergency Lighting" },
      { to: "/services/fire-extinguishers", label: "Fire Extinguishers" },
      { to: "/services/testing-certification", label: "Testing & Certification" },
      { to: "/services/maintenance", label: "Planned Maintenance" },
    ],
  },
  {
    title: "CCTV & Security",
    icon: Camera,
    links: [
      { to: "/cctv-security", label: "CCTV Installation" },
      { to: "/cctv-security#analytics", label: "Video Analytics" },
      { to: "/cctv-security#intruder", label: "Intruder Alarms" },
      { to: "/cctv-security#perimeter", label: "Perimeter Security" },
      { to: "/maintenance-support", label: "Security Maintenance" },
    ],
  },
  {
    title: "Access Control",
    icon: DoorOpen,
    links: [
      { to: "/access-control", label: "Access Control Systems" },
      { to: "/access-control#credentials", label: "Card & Fob Access" },
      { to: "/access-control#intercoms", label: "Intercoms" },
      { to: "/access-control#biometric", label: "Biometric Access" },
    ],
  },
  {
    title: "Monitoring",
    icon: Radio,
    links: [
      { to: "/monitoring#alarm", label: "Alarm Monitoring" },
      { to: "/monitoring#cctv", label: "CCTV Monitoring" },
      { to: "/monitoring#verification", label: "Remote Verification" },
      { to: "/monitoring#panic", label: "Panic Alarms" },
      { to: "/monitoring#lone-worker", label: "Lone Worker Protection" },
    ],
  },
];

export default function Footer() {
  return (
    <footer className="bg-slate-900 text-slate-300" data-testid="site-footer">
      <div className="fss-ribbon" />
      <div className="fss-container py-16">
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-10">
          <div className="lg:col-span-2">
            <div className="bg-white inline-block p-3 rounded-sm">
              <FSSLogo className="h-12 w-auto" />
            </div>
            <p className="mt-5 text-sm text-slate-400 leading-relaxed">
              FSS provides fire safety, CCTV, security, access control and monitoring solutions for businesses and organisations across Ireland.
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-red-500 shrink-0" />
                <a href="tel:018351454" className="hover:text-white" data-testid="footer-phone">01 835 1454</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-red-500 shrink-0" />
                <a href="mailto:service@fssl.ie" className="hover:text-white" data-testid="footer-email">service@fssl.ie</a>
              </li>
              <li className="flex items-center gap-3">
                <Globe className="w-4 h-4 text-red-500 shrink-0" />
                <span>www.fssl.ie</span>
              </li>
              <li className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-red-500 shrink-0" />
                <span>Nationwide · Ireland</span>
              </li>
            </ul>
          </div>

          {COLS.map((col) => (
            <div key={col.title}>
              <div className="flex items-center gap-2 mb-4">
                <col.icon className="w-4 h-4 text-green-500" />
                <div className="text-xs font-bold tracking-[0.22em] uppercase text-white">{col.title}</div>
              </div>
              <ul className="space-y-2 text-sm">
                {col.links.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="hover:text-white">{l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-slate-800 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <Link to="/about" className="hover:text-white">About FSS</Link>
          <Link to="/industries" className="hover:text-white">Industries</Link>
          <Link to="/maintenance-support" className="hover:text-white">Maintenance & Support</Link>
          <Link to="/contact" className="hover:text-white">Contact</Link>
          <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
          <Link to="/cookies" className="hover:text-white">Cookie Policy</Link>
          <Link to="/terms" className="hover:text-white">Terms & Conditions</Link>
          <Link to="/faqs" className="hover:text-white">FAQs</Link>
        </div>
      </div>

      <div className="border-t border-slate-800">
        <div className="fss-container py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <div>© {new Date().getFullYear()} Facilities and Safety Systems. All rights reserved.</div>
          <div className="flex items-center gap-4">
            <span className="font-semibold tracking-wide">Protecting People, Property and Premises</span>
            <Link to="/admin" className="text-slate-600 hover:text-slate-400 text-[10px]">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
